/**
 * SSE Chat Service Hook
 *
 * @module services/chat/sse/_hook
 *
 * @remarks
 * This hook provides a high-level interface to the SSE chat system.
 * It manages provider instances, authentication, and conversation state.
 *
 * Features:
 * - Provider caching per conversation (session_id)
 * - Automatic token injection from auth context
 * - Integration with @ant-design/x useXChat
 * - Debug mode support
 *
 * @example
 * ```typescript
 * const { provider, conversationKey } = useSSEChat({
 *   external_user_id: userId,
 *   session_id: sessionId,
 *   debug: true,
 * });
 *
 * const { onRequest, messages, isRequesting } = useXChat({
 *   provider,
 *   conversationKey,
 * });
 * ```
 */

import type { SSEChatInput, SSEChatOutput, SSEXRequestOptions } from "./_types";

import { useMemo } from "react";

import { SSEChatProvider } from "./_provider";
import { SSERequest } from "./_request";

/**
 * SSE Chat Hook
 *
 * @param config - Configuration options
 * @returns Provider instance and conversation key
 *
 * @remarks
 * The hook creates or retrieves a cached provider instance for the conversation.
 * Provider is reused across component re-renders to maintain connection state.
 *
 * **Important**: Provider instances are cached per conversation. Call
 * `clearProvider()` when conversation ends or user logs out.
 */
export const useSSEChat = (
  options: SSEXRequestOptions<SSEChatInput, SSEChatOutput>
) => {
  const { external_user_id, session_id } = options.config;

  /** Generate conversation key for useXChat */
  const conversationKey = useMemo(
    () => session_id || `new-${external_user_id}`,
    [external_user_id, session_id]
  );

  /** Provider */
  const provider = useMemo(
    () => new SSEChatProvider({ request: new SSERequest(options) }),
    [options]
  );

  return {
    /** SSE chat provider instance */
    provider,

    /** Conversation key for useXChat */
    conversationKey,
  };
};
